import { SectionTab } from "@foodpilot/foods";
import { SettingsContext, SettingsInitialValues } from "./context";
import { useSettingsLinks } from "./useSettingsLinks";
import { useMemo } from "react";
import type { ReactNode } from "react";
import { URLHandler } from "src/services/URLHandler";
import { useLocation, useNavigate } from "react-router";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { useTranslation } from "react-i18next";
import { CREATE, DELETE, MODIFY, STRATEGY, useFoodpilotContext } from "src/context/FoodpilotContext";

export type SectionTabSettings = SectionTab & {
    isSelected: boolean;
};

type SettingsProviderProps = {
    children: ReactNode;
};
export const SettingsProvider = (props: SettingsProviderProps) => {
    const { children } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const { applicationParameters } = useApplicationParameters();
    const { t } = useTranslation();
    const { canUser } = useFoodpilotContext();
    const allLinks = useSettingsLinks();

    // Group links by tabs according to Figma design
    const getTabsFromLinks = (): SectionTabSettings[] => {
        const visibleLinks = allLinks.filter((sl) => sl.visible);
        // Configuration tab - includes dashboard, cms, applications, etc.
        const getIsConfigSelected = (value: string): boolean => {
            return (
                value.includes("/configuration") ||
                value.includes("/applications") ||
                value.includes("/infos") ||
                value.includes("/applicationParameters") ||
                value.includes("/roles") ||
                // value.includes("/tags") ||
                value.includes("/csrTheme") ||
                value.includes("/consent-governance") ||
                value.includes("/translations") ||
                value.includes("/features") ||
                value.includes("/theme")
            );
        };
        const configurationLinks = visibleLinks.filter((link) => getIsConfigSelected(link.to));
        const isConfigurationSelected = getIsConfigSelected(location.pathname);

        // Organization tab - my organization page and related pages
        const getIsOrganizationSelected = (value: string): boolean => {
            return (
                value.includes("/my-organization") ||
                value.includes("/companies") ||
                value.includes("/company-sectors") ||
                value.includes("/providers") ||
                value.includes("/brands") ||
                value.includes("/product-line") ||
                value.includes("/ingredient-line") ||
                value.includes("/packagings") ||
                value.includes("/tags") ||
                value.includes("/additional-object-parameters") ||
                value.includes("/cohort-definitions")
            );
        };
        const organizationLinks = visibleLinks.filter((link) => getIsOrganizationSelected(link.to));
        const isOrganizationSelected = getIsOrganizationSelected(location.pathname);

        const getIsContentSelected = (value: string): boolean => {
            const allowedPaths = ["/content", "/cms", "/media-library", "/csrStrategy"];
            return allowedPaths.some((path) => value.includes(path));
        };

        const contentLinks = visibleLinks.filter((link) => getIsContentSelected(link.to));
        const isContentSelected = getIsContentSelected(location.pathname);
        const canConfigureStrategy =
            applicationParameters?.["FS-strategy"] === "true" &&
            (canUser(CREATE, STRATEGY) || canUser(MODIFY, STRATEGY) || canUser(DELETE, STRATEGY));

        // Users tab stays flat and opens user management directly.
        const getUserSelected = (value: string): boolean => {
            return value.includes("/users");
        };
        const userLinks = visibleLinks.filter((link) => getUserSelected(link.to));
        const isUserSelected = getUserSelected(location.pathname);

        const getReferencesSelected = (value: string): boolean => {
            return (
                value.includes("/references") ||
                value.includes("/ingredient-references") ||
                value.includes("/questionnaires") ||
                value.includes("/data-lists-values-overrides") ||
                value.includes("/entity-target-score-assignment")
            );
        };
        const referencesLinks = visibleLinks.filter((link) => getReferencesSelected(link.to));
        const isReferencesSelected = getReferencesSelected(location.pathname);

        // Suppliers/Providers tab
        // const supplierLinks = visibleLinks.filter(
        //     (link) =>
        //         link.to.includes("/providers") ||
        //         link.to.includes("/ingredient-line") ||
        //         link.to.includes("/product-line"),
        // );

        const tabs: SectionTabSettings[] = [];

        tabs.push({
            name: t("settings.platform.title", "Platform"),
            onClick: () => {
                // Navigate to dashboard by default, or first available config link
                const url = URLHandler.settings.configuration();
                // const dashboardLink = configurationLinks.find((l) => l.to === URLHandler.settings.list());
                // const url = dashboardLink ? dashboardLink.to : configurationLinks[0].to;
                navigate(url);
            },
            isSelected: isConfigurationSelected,
            disabled: configurationLinks.length === 0,
        });

        tabs.push({
            name: t("settings.organization.title", "Organization"),
            onClick: () => navigate(URLHandler.settings.myOrganization()),
            isSelected: isOrganizationSelected,
            disabled: organizationLinks.length === 0,
        });

        tabs.push({
            name: t("settings.users", "Users"),
            onClick: () => navigate(URLHandler.settings.users.list()),
            isSelected: isUserSelected,
            disabled: userLinks.length === 0,
        });

        tabs.push({
            name: t("settings.content.title", "Content"),
            onClick: () => navigate(URLHandler.settings.content()),
            isSelected: isContentSelected,
            disabled: contentLinks.length === 0 && !canConfigureStrategy,
        });

        tabs.push({
            name: t("settings.references.title", "References"),
            onClick: () => navigate(URLHandler.settings.references()),
            isSelected: isReferencesSelected,
            disabled: referencesLinks.length === 0,
        });

        return tabs;
    };

    // Calculate tabs first
    const tabs = useMemo(() => {
        // Get activated feature tabs inline
        const getActivatedFeatureTabs = (): SectionTabSettings[] => {
            if (!applicationParameters) return [];

            const featureTabs: SectionTabSettings[] = [];

            // ICV Management feature
            if (applicationParameters["FS-icv-management"] === "true") {
                featureTabs.push({
                    name: "Gestion des ICVs",
                    isSelected: location.pathname.includes("/settings/icvs"),
                    onClick: () => navigate("/settings/icvs"),
                });
            }

            return featureTabs;
        };

        const allTabs = getTabsFromLinks();
        const filteredTabs = allTabs.filter((i) => !i.disabled);
        const featureTabs = getActivatedFeatureTabs();

        // Insert feature tabs before Questionnaire tab
        filteredTabs.splice(-1, 0, ...featureTabs);

        return filteredTabs;
    }, [allLinks, applicationParameters, location.pathname, navigate, t]);

    // It will find the first occurence of a tab selected;
    // else user choose defaults.
    const _selectedTabIndex = tabs.findIndex((i) => i.isSelected === true);

    const items: SettingsInitialValues = {
        allLinks,

        tabs,
        selectedTabIndex: _selectedTabIndex === -1 ? undefined : _selectedTabIndex,
    };

    return <SettingsContext.Provider value={items}>{children}</SettingsContext.Provider>;
};
